import { __awaiter, __generator, __assign } from 'tslib';
import React__default, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { KpkApiThumbnailTemplateFormat } from '@keepeek/api-client';
import { thumbnailGenerationServiceConfigurationSelector, MediaLinksField, ThumbnailGenerationSourceMediaFormatEnum } from '@keepeek/commons';
import { Paper, Box, Typography, Chip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import clsx from 'clsx';
import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import DefaultIcon from '../../DefaultIcon/index.js';
import KCrop from '../../KCrop/index.js';
import { withSuspense } from '../../withSuspense/index.js';
import { useCroppedImageFromService } from '../hooks.js';
import ThumbnailTemplateActions from '../ThumbnailTemplateActions/index.js';
import { adaptCropForImageService } from './utils.js';

var Crop = function (_a) {
    var _this = this;
    var className = _a.className, id = _a.id, _b = _a.testId, testId = _b === void 0 ? "KFThumbnailGeneratorCrop" : _b, sx = _a.sx, _c = _a.enabledActions, enabledActions = _c === void 0 ? ["copy", "open", "download"] : _c, element = _a.element, outWidth = _a.outWidth, outHeight = _a.outHeight, onCropChange = _a.onCropChange, onInsert = _a.onInsert, _d = _a.KIcon, KIcon = _d === void 0 ? DefaultIcon : _d, addExtensionAtEnd = _a.addExtensionAtEnd, aspectRatio = _a.aspectRatio;
    var t = useTranslation("refront-components").t;
    var techProThumbnailGeneration = useRecoilValue(thumbnailGenerationServiceConfigurationSelector);
    var enqueueSnackbar = useSnackbar().enqueueSnackbar;
    var previewUrl = element._links &&
        element._links[MediaLinksField.WHR] &&
        element._links[MediaLinksField.WHR].href;
    var _e = useState(), imageForActions = _e[0], setImageForActions = _e[1];
    var _f = useState(KpkApiThumbnailTemplateFormat.Jpeg), outFormat = _f[0], setOutFormat = _f[1];
    var cropperRef = useRef();
    var _g = useState(), imageNaturalSize = _g[0], setImageNaturalSize = _g[1];
    var _h = useState(), currentAdaptedCrop = _h[0], setCurrentAdaptedCrop = _h[1];
    var getCroppedImageFromService = useCroppedImageFromService();
    var debouncedSetImageForActions = useMemo(function () {
        return debounce(function (element, outFormat, outWidth, outHeight, adaptedCropWidth, adaptedCropHeight, adaptedCropX, adaptedCropY) { return __awaiter(_this, void 0, void 0, function () {
            var result;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getCroppedImageFromService({
                            outFormat: outFormat,
                            element: element,
                            outWidth: outWidth,
                            outHeight: outHeight,
                            cropWidth: adaptedCropWidth,
                            cropHeight: adaptedCropHeight,
                            cropX: adaptedCropX,
                            cropY: adaptedCropY,
                            addExtensionAtEnd: addExtensionAtEnd
                        })];
                    case 1:
                        result = _a.sent();
                        setImageForActions(result);
                        return [2 /*return*/];
                }
            });
        }); }, 300);
    }, [addExtensionAtEnd, getCroppedImageFromService]);
    var handleCropChange = useCallback(function (data, currentImageNaturalSize) {
        if (!currentImageNaturalSize) {
            // image size is not yet available => do not handle crop change !
            return;
        }
        var _a = adaptCropForImageService({
            height: data.height,
            width: data.width,
            x: data.x,
            y: data.y,
            element: element,
            previewHeight: currentImageNaturalSize.height,
            previewWidth: currentImageNaturalSize.width,
            serviceImageSourceMode: techProThumbnailGeneration["source-media-format"] ||
                ThumbnailGenerationSourceMediaFormatEnum.UHD
        }), adaptedCropWidth = _a.width, adaptedCropHeight = _a.height, adaptedCropX = _a.x, adaptedCropY = _a.y;
        setCurrentAdaptedCrop({
            width: adaptedCropWidth,
            height: adaptedCropHeight,
            x: adaptedCropX,
            y: adaptedCropY
        });
        debouncedSetImageForActions(element, outFormat, outWidth || 0, outHeight || 0, adaptedCropWidth, adaptedCropHeight, adaptedCropX, adaptedCropY);
        onCropChange && onCropChange(adaptedCropWidth, adaptedCropHeight);
    }, [
        element,
        techProThumbnailGeneration,
        debouncedSetImageForActions,
        outFormat,
        outWidth,
        outHeight,
        onCropChange,
    ]);
    var handleReady = useCallback(function (cropper) {
        setImageNaturalSize({
            width: cropper.getImageData().naturalWidth,
            height: cropper.getImageData().naturalHeight
        });
        cropperRef.current = cropper;
        // init IHM crop value from crop lib :
        handleCropChange(cropper.getData(), {
            width: cropper.getImageData().naturalWidth,
            height: cropper.getImageData().naturalHeight
        });
    }, [handleCropChange]);
    var handleDownload = function () { return __awaiter(_this, void 0, void 0, function () {
        var croppedImage;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!currentAdaptedCrop) {
                        return [2 /*return*/];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, getCroppedImageFromService({
                            outFormat: outFormat,
                            element: element,
                            outWidth: outWidth,
                            outHeight: outHeight,
                            cropWidth: currentAdaptedCrop.width,
                            cropHeight: currentAdaptedCrop.height,
                            cropX: currentAdaptedCrop.x,
                            cropY: currentAdaptedCrop.y,
                            downloadPrefixName: "crop",
                            addExtensionAtEnd: addExtensionAtEnd
                        })];
                case 2:
                    croppedImage = _a.sent();
                    window.open(croppedImage);
                    return [3 /*break*/, 4];
                case 3:
                    _a.sent();
                    enqueueSnackbar(t("components.kf-thumbnail-generator.preview.download-link.error"), {
                        variant: "error"
                    });
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var getFormattedSourceMediaFormat = function () {
        var sourceMediaFormat = techProThumbnailGeneration["source-media-format"] ||
            ThumbnailGenerationSourceMediaFormatEnum.UHD;
        switch (sourceMediaFormat) {
            case ThumbnailGenerationSourceMediaFormatEnum.UHD:
                return "4K";
            case ThumbnailGenerationSourceMediaFormatEnum.WHR:
                return "WHR";
        }
        throw new Error("sourceMediaFormat ".concat(sourceMediaFormat, " not implemented"));
    };
    var handleFormatChange = function (_, value) { return __awaiter(_this, void 0, void 0, function () {
        var result;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setOutFormat(value);
                    if (!currentAdaptedCrop) return [3 /*break*/, 2];
                    return [4 /*yield*/, getCroppedImageFromService({
                            outFormat: value,
                            element: element,
                            outWidth: outWidth,
                            outHeight: outHeight,
                            cropWidth: currentAdaptedCrop === null || currentAdaptedCrop === void 0 ? void 0 : currentAdaptedCrop.width,
                            cropHeight: currentAdaptedCrop === null || currentAdaptedCrop === void 0 ? void 0 : currentAdaptedCrop.height,
                            cropX: currentAdaptedCrop.x,
                            cropY: currentAdaptedCrop.y,
                            addExtensionAtEnd: addExtensionAtEnd
                        })];
                case 1:
                    result = _a.sent();
                    setImageForActions(result);
                    _a.label = 2;
                case 2: return [2 /*return*/];
            }
        });
    }); };
    // listen for out size change to trigger crop change and get new cropped signed image from service :
    useEffect(function () {
        var _a;
        if (!outHeight || !outWidth || !cropperRef.current) {
            return;
        }
        handleCropChange((_a = cropperRef.current) === null || _a === void 0 ? void 0 : _a.getData(), imageNaturalSize);
    }, [handleCropChange, imageNaturalSize, outHeight, outWidth]);
    return (React__default.createElement(Paper, { id: id, "data-testid": testId, className: clsx("KFThumbnailGeneratorCrop", className), elevation: 1, variant: "elevation", sx: __assign(__assign({}, sx), { width: "100%", height: "100%", display: "flex", flexDirection: "column", p: 2 }) },
        React__default.createElement(Box, { sx: {
                display: "flex",
                alignItems: "flex-end",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: 0.8,
                pb: 0.8
            } },
            React__default.createElement(Box, { sx: {
                    flexGrow: 1
                } },
                React__default.createElement(Typography, { variant: "h6" }, t("components.kf-thumbnail-generator.crop.title")),
                React__default.createElement(Box, { sx: {
                        gap: 0.8,
                        display: "flex"
                    } },
                    !outWidth && !outHeight && (React__default.createElement(Chip, { label: t("components.kf-thumbnail-generator.crop.chip.sourceMediaFormat", {
                            format: getFormattedSourceMediaFormat()
                        }), size: "small", variant: "outlined", color: "primary" })),
                    (outWidth || (currentAdaptedCrop === null || currentAdaptedCrop === void 0 ? void 0 : currentAdaptedCrop.width)) && (outHeight || (currentAdaptedCrop === null || currentAdaptedCrop === void 0 ? void 0 : currentAdaptedCrop.height)) && (React__default.createElement(Chip, { label: t("components.kf-thumbnail-generator.crop.chip.outSize.label", {
                            width: outWidth || (currentAdaptedCrop === null || currentAdaptedCrop === void 0 ? void 0 : currentAdaptedCrop.width),
                            height: outHeight || (currentAdaptedCrop === null || currentAdaptedCrop === void 0 ? void 0 : currentAdaptedCrop.height)
                        }), title: t("components.kf-thumbnail-generator.crop.chip.outSize.title", {
                            width: outWidth || (currentAdaptedCrop === null || currentAdaptedCrop === void 0 ? void 0 : currentAdaptedCrop.width),
                            height: outHeight || (currentAdaptedCrop === null || currentAdaptedCrop === void 0 ? void 0 : currentAdaptedCrop.height)
                        }), size: "small", variant: "outlined", color: "primary" })),
                    React__default.createElement(QualityErrorDetected, { KIcon: KIcon, outWidth: outWidth, outHeight: outHeight, cropWidth: currentAdaptedCrop === null || currentAdaptedCrop === void 0 ? void 0 : currentAdaptedCrop.width, cropHeight: currentAdaptedCrop === null || currentAdaptedCrop === void 0 ? void 0 : currentAdaptedCrop.height }))),
            React__default.createElement(Box, { sx: {
                    gap: 4,
                    display: "flex",
                    justifyContent: "space-between"
                } },
                React__default.createElement(Box, null,
                    React__default.createElement(ToggleButtonGroup, { color: "primary", exclusive: true, value: outFormat, onChange: handleFormatChange, sx: {
                            height: "30px"
                        } }, Object.values(KpkApiThumbnailTemplateFormat)
                        .filter(function (v) { return isNaN(Number(v)); })
                        .map(function (value) { return (React__default.createElement(ToggleButton, { key: value, value: value }, value)); }))),
                React__default.createElement(Box, { className: "KFThumbnailGeneratorCrop-actions", sx: {
                        gap: 0.8,
                        display: "flex",
                        alignItems: "center"
                    } },
                    React__default.createElement(ThumbnailTemplateActions, { enabledActions: enabledActions, onInsert: onInsert, KIcon: KIcon, element: element, image: imageForActions, onDownload: handleDownload })))),
        React__default.createElement(Box, { sx: {
                display: "flex",
                flex: 1,
                overflow: "hidden",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "5px",
                backgroundColor: function (theme) { return theme.palette.grey[200]; }
            } }, previewUrl && (React__default.createElement(KCrop, { sx: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%"
            }, imgSrc: previewUrl, imgAlt: element.title || "", onCropChange: function (cropData) { return handleCropChange(cropData, imageNaturalSize); }, onReady: handleReady, aspectRatio: aspectRatio, zoomable: false, rotatable: false, toggleDragModeOnDblclick: false, background: false, viewMode: 1 })))));
};
var DEGRADATION_RATIO = 150 / 100; // display degradation warning when 50% of degradation
var QualityErrorDetected = function (_a) {
    var _b = _a.KIcon, KIcon = _b === void 0 ? DefaultIcon : _b, outWidth = _a.outWidth, outHeight = _a.outHeight, cropWidth = _a.cropWidth, cropHeight = _a.cropHeight;
    var t = useTranslation("refront-components").t;
    if (!outWidth ||
        !outHeight ||
        !cropWidth ||
        !cropHeight ||
        cropWidth * DEGRADATION_RATIO > outWidth ||
        cropHeight * DEGRADATION_RATIO > outHeight) {
        return null;
    }
    return (React__default.createElement(Chip, { label: t("components.kf-thumbnail-generator.crop.chip.qualityErrorDetected.label"), title: t("components.kf-thumbnail-generator.crop.chip.qualityErrorDetected.title"), size: "small", variant: "outlined", color: "error", icon: React__default.createElement(KIcon, { sx: { fontSize: "15px", paddingLeft: "4px" }, className: "fa-triangle-exclamation" }) }));
};
var Crop$1 = withSuspense(Crop);

export { Crop$1 as default };
//# sourceMappingURL=index.js.map
